import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAuth } from '@/presentation/hooks/use-auth.js';
import { MicrosoftAccessNotice } from '@/presentation/pages/auth/components/microsoft-access-notice.js';
import { LoginPage } from '@/presentation/pages/auth/login-page.js';
import { parseMicrosoftLoginHash, shouldAutoStartMicrosoftLogin, startMicrosoftLogin } from '@/presentation/pages/auth/utils/microsoft-login.js';
import { ROUTES } from '@/shared/constants/routes.js';

interface Props {
    controller: AuthController;
}

export function MicrosoftLoginPage({ controller }: Props) {
    const { translate, useAuthStore } = useApp();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const navigate = useNavigate();
    const { redeemMicrosoft } = useAuth(controller);
    const hash = useMemo(() => parseMicrosoftLoginHash(window.location.hash), []);
    const [enabled, setEnabled] = useState(false);
    const [ready, setReady] = useState(false);
    const [redeemFailed, setRedeemFailed] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        let active = true;
        controller.getEntraOptions().then((result) => {
            if (!active) {
                return;
            }
            if (result.isRight()) {
                setEnabled(result.value.enabled);
            }
            setReady(true);
        });
        return () => {
            active = false;
        };
    }, [controller]);

    const autoStart = ready && shouldAutoStartMicrosoftLogin({ userAgent: navigator.userAgent, hasSession: isAuthenticated, enabled, hash });

    useEffect(() => {
        if (!autoStart || started.current) {
            return;
        }
        started.current = true;
        startMicrosoftLogin();
    }, [autoStart]);

    useEffect(() => {
        if (hash.kind !== 'ticket') {
            return;
        }
        redeemMicrosoft(hash.ticket).then((ok) => {
            if (ok) {
                navigate(ROUTES.DASHBOARD, { replace: true });
                return;
            }
            setRedeemFailed(true);
        });
    }, [hash, redeemMicrosoft, navigate]);

    const holdForm = hash.kind === 'empty' && navigator.userAgent.includes('Orbit/') && !isAuthenticated && (!ready || autoStart);

    if (holdForm) {
        return <CircularProgress data-testid="microsoft-login-waiting" />;
    }

    const notice =
        hash.kind === 'denied' ? (
            <MicrosoftAccessNotice template={translate('auth.microsoftDenied')} email={hash.email} />
        ) : hash.kind === 'failed' || redeemFailed ? (
            <MicrosoftAccessNotice template={translate('auth.microsoftFailed')} />
        ) : null;

    return <LoginPage controller={controller} notice={notice} />;
}
